import { useReactiveVar } from '@apollo/client';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ShiftWeekList from './ShiftWeekList';

import { LtaGigMenu } from '@/components/LtaGigMenu';
import Page from '@/components/Page';
import useAuth from '@/hooks/useAuth';
import useMediaQuery from '@/hooks/useMediaQuery';
import useOrderFlowV2Enabled from '@/hooks/useOrderFlowV2Enabled';
import { CurrentCustomerAdminFragment, JobTypeEnum } from '@/types/graphql';
import { hasValidCustomerAccountStatus } from '@/util/accountstatus';
import { currentAdminVar } from '@/util/apollo/cache';
import ls, { ACCOUNT_DISABLED } from '@/util/localstorage';
import { trackOrderCreationStarted } from '@/util/vexo';

const Dashboard = () => {
  const accountDisabled = ls.get(ACCOUNT_DISABLED) === 'true';
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const isWideScreen = useMediaQuery('(min-width: 1024px)');
  const orderFlowV2Enabled = useOrderFlowV2Enabled();
  const useV2Entry = isWideScreen && orderFlowV2Enabled;
  const currentAdminUser = useReactiveVar(
    currentAdminVar,
  ) as CurrentCustomerAdminFragment;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const ref = useRef(null);
  const [anchorElV2, setAnchorElV2] = useState<null | HTMLElement>(null);
  const openV2 = Boolean(anchorElV2);
  const refV2 = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const isFeatFlagDisabledCustomerLoginAllowedEnabled = useFeatureValue(
    'disabled-customer-login',
    false,
  );

  const orderFolder = 'creategigorder';

  function hasValidAccountStatus() {
    return hasValidCustomerAccountStatus(
      isFeatFlagDisabledCustomerLoginAllowedEnabled,
      currentAdminUser,
      accountDisabled,
    );
  }

  const dashboardOnNewOrder = () => {
    if (
      isCurrentCustomerAdmin(currentAdmin) &&
      !currentAdmin.customer?.ltaAllowed
    ) {
      trackOrderCreationStarted({ order_type: 'GIG' });
      navigate(`/orders/${orderFolder}?type=${JobTypeEnum.GIG}`);
    } else {
      setAnchorEl(ref?.current);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const dashboardOnNewOrderV2 = () => {
    if (
      isCurrentCustomerAdmin(currentAdmin) &&
      !currentAdmin.customer?.ltaAllowed
    ) {
      trackOrderCreationStarted({ order_type: 'GIG' });
      navigate(`/orders-v2/create?type=${JobTypeEnum.GIG}`);
    } else {
      setAnchorElV2(refV2?.current);
    }
  };

  const handleCloseMenuV2 = () => {
    setAnchorElV2(null);
  };

  return (
    <Page
      headerPadding={phoneOnly}
      noPadding={phoneOnly}
      primaryAction={{
        a11yLabel: 'Go to create order',
        label: 'Place new order',
        onAction: useV2Entry ? dashboardOnNewOrderV2 : dashboardOnNewOrder,
        id: useV2Entry ? 'create-new-order-v2' : 'create-new-order',
        ref: useV2Entry ? refV2 : ref,
        disabled: !hasValidAccountStatus(),
      }}
      size={phoneOnly ? 'full' : 'xl'}
      title="Dashboard"
    >
      <LtaGigMenu
        anchorEl={anchorEl}
        handleCloseMenu={handleCloseMenu}
        open={open}
      />
      {orderFlowV2Enabled && (
        <LtaGigMenu
          anchorEl={anchorElV2}
          handleCloseMenu={handleCloseMenuV2}
          open={openV2}
          useV2
        />
      )}
      <ShiftWeekList />
    </Page>
  );
};

export default Dashboard;
